import { formatMoney } from '../../components/data-grid/money';
import { sheetOf } from '../../components/report-grid/export';
import type { ExportColumn } from '../../components/report-grid/export';
import { ReportGrid } from '../../components/report-grid/ReportGrid';
import type { ReportGridColumn } from '../../components/report-grid/ReportGrid';
import { useI18n } from '../../i18n/I18nProvider';
import type { Language } from '../../i18n/catalogs';
import { localizedName } from './reports.api';
import type { TrialBalanceReport, TrialBalanceRow } from './reports.api';
import { ReportFrame } from './ReportFrame';
import { checkBalance, sumLeafSideTotals, trialBalanceSheetName } from './totals';

export interface TrialBalanceViewProps {
  report: TrialBalanceReport;
  language: Language;
  periodLabel: string;
}

const AMOUNT_KEYS = [
  'openingDt',
  'openingCt',
  'turnoverDt',
  'turnoverCt',
  'closingDt',
  'closingCt',
] as const;

export function TrialBalanceView({ report, language, periodLabel }: TrialBalanceViewProps): JSX.Element {
  const { t } = useI18n();
  const totals = sumLeafSideTotals(report.rows);
  const leafCheck = checkBalance(totals, language);
  const check = leafCheck.balanced ? checkBalance(report.totals, language) : leafCheck;

  const amountHeader = (key: (typeof AMOUNT_KEYS)[number]): string => {
    const labels: Record<(typeof AMOUNT_KEYS)[number], string> = {
      openingDt: t('reportsOpeningDt'),
      openingCt: t('reportsOpeningCt'),
      turnoverDt: t('reportsTurnoverDt'),
      turnoverCt: t('reportsTurnoverCt'),
      closingDt: t('reportsClosingDt'),
      closingCt: t('reportsClosingCt'),
    };
    return labels[key];
  };

  const amountCell = (row: TrialBalanceRow, key: (typeof AMOUNT_KEYS)[number]): string =>
    formatMoney(row[key], language);

  const columns: Array<ReportGridColumn<TrialBalanceRow>> = [
    {
      key: 'accountCode',
      header: t('reportsCode'),
      render: (row) => (
        <span className="report__account" style={{ paddingLeft: `${Math.max(row.level - 1, 0) * 14}px` }}>
          {row.accountCode}
        </span>
      ),
      exportValue: (row) => row.accountCode,
    },
    {
      key: 'name',
      header: t('reportsAccountName'),
      render: (row) => <span className={row.isGroup ? 'report__group-name' : undefined}>{localizedName(row, language)}</span>,
      exportValue: (row) => localizedName(row, language),
    },
    ...AMOUNT_KEYS.map((key) => ({
      key,
      header: amountHeader(key),
      align: 'right' as const,
      numeric: true,
      render: (row: TrialBalanceRow) => amountCell(row, key),
      exportValue: (row: TrialBalanceRow) => row[key],
    })),
  ];

  const exportColumns: Array<ExportColumn<TrialBalanceRow>> = columns.map((column) => ({
    header: column.header,
    numeric: column.numeric,
    exportValue: column.exportValue,
  }));

  const totalsRow = [
    t('reportsTotals'),
    '',
    ...AMOUNT_KEYS.map((key) => formatMoney(totals[key], language)),
  ];

  const sheet = sheetOf(
    trialBalanceSheetName(report),
    exportColumns,
    report.rows,
    [t('reportsTotals'), '', ...AMOUNT_KEYS.map((key) => totals[key])],
  );

  return (
    <ReportFrame
      title={t('reportsTrialBalance')}
      subtitle={t('reportsTrialBalanceSubtitle')}
      periodLabel={periodLabel}
      sheet={sheet}
    >
      <div
        className={`report__badge ${check.balanced ? 'report__badge--ok' : 'report__badge--bad'}`}
        data-testid="report-balance-badge"
      >
        <span>{check.balanced ? t('reportsBalanced') : t('reportsUnbalanced')}</span>
        <span className="report__badge-detail">
          {t('reportsDifference')}: {check.difference} {t('reportsCurrency')}
        </span>
      </div>

      <ReportGrid
        columns={columns}
        rows={report.rows}
        getRowKey={(row) => row.accountCode}
        totals={totalsRow}
        emptyText={t('reportsEmpty')}
        stickyFirstColumn
        testId="trial-balance"
      />

      <p className="report__note">
        {t('reportsOffBalance')}: {formatMoney(report.offBalanceTotals.turnoverDt, language)} /{' '}
        {formatMoney(report.offBalanceTotals.turnoverCt, language)}
      </p>
    </ReportFrame>
  );
}
